import { inject, Injectable, OnDestroy, WritableSignal } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { debounceTime, BehaviorSubject, filter, Subscription } from 'rxjs';
import { SearchEnum } from '../../../core/common/enums/search.enum';

export function initialSearchTerm(): string {
  return inject(SearchService).currentQuery();
}

@Injectable({
  providedIn: 'root',
})
export class SearchService implements OnDestroy {
  private readonly router = inject(Router);
  private readonly searchByPath = new Map<string, string>();
  private readonly searchSubject = new BehaviorSubject<string>(
    this.seedInitialSearch()
  );
  private readonly subscription = new Subscription();
  readonly search$ = this.searchSubject.asObservable();

  constructor() {
    this.subscription.add(
      this.searchSubject.pipe(debounceTime(300)).subscribe(query => {
        this.syncKeywordOnUrl(query);
      })
    );
    this.subscription.add(
      this.router.events
        .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
        .subscribe(() => this.applySearchForCurrentPath())
    );
  }

  private seedInitialSearch(): string {
    const initial = this.readKeywordFromUrl();
    if (initial) {
      this.searchByPath.set(this.pathKey(), initial);
    }
    return initial;
  }

  currentQuery(): string {
    return this.searchByPath.get(this.pathKey()) || '';
  }

  search(query: string) {
    this.searchByPath.set(this.pathKey(), query);
    this.searchSubject.next(query);
  }

  clearSearch() {
    this.searchByPath.set(this.pathKey(), '');
    this.searchSubject.next('');
  }

  isMinLength(term: string): boolean {
    return (term?.length || 0) >= SearchEnum.minSearchLength;
  }

  checkSearchLength(search: string) {
    return this.isMinLength(search) ? search : '';
  }

  shouldReload(term: string, wasMinLength: boolean): boolean {
    return this.isMinLength(term) || wasMinLength;
  }

  applySearchTerm(
    term: string,
    isActiveSearch: WritableSignal<boolean>
  ): boolean {
    const shouldReload = this.shouldReload(term, isActiveSearch());
    isActiveSearch.set(this.isMinLength(term));
    return shouldReload;
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
    this.searchSubject.complete();
  }

  private applySearchForCurrentPath(): void {
    const query = this.currentQuery();
    if (this.searchSubject.value !== query) {
      this.searchSubject.next(query);
    }
    this.syncKeywordOnUrl(query);
  }

  private syncKeywordOnUrl(query: string): void {
    const urlTree = this.router.parseUrl(this.router.url);
    const current = urlTree.queryParams[SearchEnum.keyword] || '';
    if (current === query) {
      return;
    }
    if (query) {
      urlTree.queryParams[SearchEnum.keyword] = query;
    } else {
      delete urlTree.queryParams[SearchEnum.keyword];
    }
    this.router.navigateByUrl(urlTree, { replaceUrl: true });
  }

  private readKeywordFromUrl(): string {
    return (
      this.router.parseUrl(this.router.url).queryParams[SearchEnum.keyword] || ''
    );
  }

  private pathKey(): string {
    const navigation = this.router.getCurrentNavigation();
    const raw = navigation?.finalUrl
      ? this.router.serializeUrl(navigation.finalUrl)
      : this.router.url;
    return raw.split('?')[0];
  }
}
